/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Oct 13 2023
 **/
import { useEffect, useMemo } from "react";

import { _T } from "@rfice/language";
import { Tab, Typography, useTab, useModal } from "@/frameworks/designStuff/components";
import { Configure, Environments } from "@/frameworks/libraries/utilities";
import { ModalID } from "@/frameworks/core/commons";
import { Maybe } from "@/frameworks/libraries";
import { DeviceManager } from "@rsupport/devicemanager";
import { useSpace } from "@/frameworks/core/API";

import MyInfo from "./settings/MyInfo/MyInfo";
import Device from "./settings/Device/Device";
import Graphic from "./settings/Graphic/Graphic";
import Language from "./settings/Language/Language";
import Account from "./settings/Account/Account";
import LoginInfo from "./settings/Account/LoginInfo";
import Alarm from "./settings/Alarm/Alarm";
import PrepareDevice from "./settings/Device/PrepareDevice";
import Experimental from "./settings/Experimental/Experimental";
import { depthWrapperStyle, tabItemStyle, tabStyle, tabTwoDepthItemStyle } from "./emotion";
import * as S from "./emotion";

export default function SettingContainer() {
	const { handleSelectTab, currentId } = useTab();
	const modal = useModal();
	const spaceAPI = useSpace();

	const fetchMySpacesList = async () => {
		await spaceAPI.mySpaces.list.execute();
	};
	useEffect(() => {
		fetchMySpacesList();
	}, []);

	const on_device_clicked = async () => {
		await DeviceManager.instance.checking_device_permission(true, {
			before_get_media_stream() {
				handleSelectTab("prepare_device");
			}
		});
		handleSelectTab("device");
	};

	const displayAdminMenu = useMemo(() => {
		return (
			spaceAPI.mySpaces.list.result.data?.groups?.length &&
			spaceAPI.mySpaces.list.result.data?.groups.filter((group) => group.groupAuthority === "GROUP_ADMIN").length
		);
	}, [spaceAPI.mySpaces.list.result.data?.groups]);

	return (
		<>
			<S.SettingTab gap={24} verticalAlign="distribute">
				<S.SettingTabItem>
					<Typography.p typoSize="Head6">{_T("내 설정")}</Typography.p>
					<Tab aspect="column" underLine={false} css={tabStyle}>
						<S.TabItem id="my" selected={currentId === "my"} css={tabItemStyle}>
							<Typography.p typoSize="Body3_MD">{_T("My")}</Typography.p>
						</S.TabItem>
						<S.TabItem
							id="device"
							selected={currentId === "device"}
							css={tabItemStyle}
							onClick={() => {
								on_device_clicked();
							}}
						>
							<Typography.p typoSize="Body3_MD">{_T("장치 설정")}</Typography.p>
						</S.TabItem>
						<S.TabItem id="graphics" selected={currentId === "graphics"} css={tabItemStyle}>
							<Typography.p typoSize="Body3_MD">{_T("그래픽 설정")}</Typography.p>
						</S.TabItem>
						<S.TabItem
							id="alarm"
							onClick={() => {
								handleSelectTab("alarm");
							}}
							selected={currentId === "alarm"}
							css={tabItemStyle}
						>
							<Typography.p typoSize="Body3_MD">{_T("알림 설정")}</Typography.p>
						</S.TabItem>
						<S.TabItem
							onClick={() => {
								handleSelectTab("account");
							}}
							depth="two"
						>
							<Typography.p typoSize="Body3_MD">{_T("계정")}</Typography.p>
							<ul css={depthWrapperStyle}>
								<S.TabItem id="account" selected={currentId === "account"} css={tabTwoDepthItemStyle}>
									<Typography.p typoSize="Body4">{_T("계정 설정")}</Typography.p>
								</S.TabItem>
								<S.TabItem
									id="loginInfo"
									selected={currentId === "loginInfo"}
									css={tabTwoDepthItemStyle}
								>
									<Typography.p typoSize="Body5">{_T("로그인 정보")}</Typography.p>
								</S.TabItem>
							</ul>
						</S.TabItem>
						<S.TabItem id="language" selected={currentId === "language"} css={tabItemStyle}>
							<Typography.p typoSize="Body3_MD">{_T("언어 및 지역")}</Typography.p>
						</S.TabItem>
						<Maybe test={displayAdminMenu}>
							<S.TabItem
								id="admin"
								selected={currentId === "admin"}
								css={tabItemStyle}
								onClick={() => {
									modal.hide(ModalID.SETTINGS);
									window.open(`/admin/group/list`, "_blank");
								}}
							>
								<Typography.p typoSize="Body3_MD">{_T("관리자 페이지")}</Typography.p>
							</S.TabItem>
						</Maybe>
						<Maybe test={Configure.use_experimental_feature}>
							<S.TabItem id="experimental" selected={currentId === "experimental"} css={tabItemStyle}>
								<Typography.p typoSize="Body3_MD">{_T("실험실")}</Typography.p>
							</S.TabItem>
						</Maybe>
					</Tab>
				</S.SettingTabItem>
				<Maybe test={Boolean((Environments.env("ROOT_VERSION") as string)?.length > 0)}>
					<S.SettingTabItem>
						<Typography.p>v{Environments.env("ROOT_VERSION")}</Typography.p>
					</S.SettingTabItem>
				</Maybe>
			</S.SettingTab>
			<S.SettingContents>
				<S.TabContent activeId="my">
					<MyInfo spaceData={spaceAPI.mySpaces.list.result.data} />
				</S.TabContent>
				<S.TabContent activeId="device">
					<Device />
				</S.TabContent>
				<S.TabContent activeId="prepare_device">
					<PrepareDevice />
				</S.TabContent>
				<S.TabContent activeId="graphics">
					<Graphic />
				</S.TabContent>
				<S.TabContent activeId="alarm">
					<Alarm />
				</S.TabContent>
				<S.TabContent activeId="account">
					<Account />
				</S.TabContent>
				<S.TabContent activeId="loginInfo">
					<LoginInfo />
				</S.TabContent>
				<S.TabContent activeId="language">
					<Language />
				</S.TabContent>
				<S.TabContent activeId="experimental">
					<Experimental />
				</S.TabContent>
			</S.SettingContents>
		</>
	);
}
